import { isSettingsDeferredOwnerPath } from './settings-deferred-owner';
import { USER_ROLE_VALUES } from '@statura/types';
import { B2G_VIEW_ROLES, B2G_SUBMIT_ROLES } from '@/lib/integrations/government/b2g-access';
import {
  SETTINGS_OWN_NOTIFICATION_ROLES,
  SETTINGS_TENANT_ADMIN_ROLES,
} from './settings-access-roles';
import { findClusterForPath } from '@/components/nav-v2/settings/settings-ia';
import type { SettingsDelegationPreference } from './settings-delegation';
type Classification = 'E' | 'R' | 'N';
type ControlAuthority = {
  roles: readonly string[];
  /** Actual action / SQL enforcement remains mandatory at execution. */
  authority: string;
  /**
   * Main's server command admitted this cohort without page admission (role
   * cohort + RPC only). Persisted denials and hidden leaves still narrow it.
   */
  pageIndependentCommand?: true;
};
const ADMIN = SETTINGS_TENANT_ADMIN_ROLES;

/** Exact leaves only: children must never inherit delegated mutation authority. */
export const SETTINGS_DELEGATED_AUTHORITY: Readonly<
  Record<
    string,
    {
      classification: Readonly<Record<string, Classification>>;
      controls: Readonly<Record<string, ControlAuthority>>;
    }
  >
> = {
  '/settings/notification-preferences': {
    classification: {},
    controls: {
      updateOwnNotificationPreferences: {
        roles: SETTINGS_OWN_NOTIFICATION_ROLES,
        authority:
          'upsertNotificationPreference; upsert_my_notification_preference_controlled; original authenticated internal-role cohort and own user/tenant SQL scope, mandatory channel locks',
      },
      updateOwnRegisterPreferences: {
        roles: SETTINGS_OWN_NOTIFICATION_ROLES,
        authority:
          'runCallerSessionCommandAction registerPreferences.set; set_my_compliance_register_preference_controlled; original internal-role own account cohort, requireControlledRpcCaller and mandatory SIRS/data-breach in-app channels',
      },
    },
  },
  '/settings': {
    classification: {},
    controls: {
      manageFamilyAppSettings: {
        roles: ['TENANT_ADMIN'],
        authority:
          'familyAccessAction FAMILY_SETTINGS_SET/FAMILY_POLICY_SET; consumer_family_settings_set_controlled / consumer_family_policy_set_controlled; existing tenant administrator, tenant, consent/privacy ceiling and revision checks',
      },
    },
  },
  '/settings/facility-config': {
    classification: { FACILITY_MANAGER: 'E', REGION_MANAGER: 'R' },
    controls: {
      upsertFacilityConfig: {
        roles: [...ADMIN, 'FACILITY_MANAGER'],
        authority: 'upsert_facility_config_controlled + assert_staff_facility_scope_allowed',
      },
    },
  },
  '/settings/notifications': {
    classification: { COORDINATOR: 'E', FACILITY_MANAGER: 'E' },
    controls: Object.fromEntries(
      ['createNotificationTemplate', 'updateNotificationTemplate', 'ownLayoutPreferences'].map(
        (id) => [
          id,
          {
            roles: [...ADMIN, 'COORDINATOR', 'FACILITY_MANAGER'],
            authority:
              id === 'ownLayoutPreferences'
                ? 'layout_preferences own user row: insert/update/delete; session tenant/user RLS'
                : 'notification_templates_manage: session tenant_id USING / WITH CHECK',
          },
        ],
      ),
    ),
  },
  '/settings/workflows/closeout': {
    classification: { QUALITY_OFFICER: 'E', CLINICAL_LEAD: 'E' },
    controls: {},
  },
  '/settings/approvals': {
    classification: {},
    controls: {
      createApprovalRule: {
        roles: [...ADMIN, 'FACILITY_MANAGER'],
        authority: 'create_approval_rule_controlled; approval_rules',
      },
      updateApprovalRule: {
        roles: [...ADMIN, 'FACILITY_MANAGER'],
        authority: 'update_approval_rule_controlled; approval_rules',
      },
      respondToApprovalRequest: {
        roles: [...ADMIN, 'FACILITY_MANAGER', 'COORDINATOR', 'QUALITY_OFFICER'],
        authority: 'respond_approval_request_controlled; approval_requests',
      },
      ownLayoutPreferences: {
        roles: ADMIN,
        authority: 'layout_preferences own user row: insert/update/delete; session tenant/user RLS',
      },
    },
  },
  '/settings/organisation': {
    classification: {},
    controls: {
      setSoloOperatorTenantFlag: {
        roles: ADMIN,
        authority:
          'setSoloOperatorTenantFlagAction; set_solo_operator_tenant_flag_controlled; original tenant.configure cohort and tenant guard',
      },
      updateOrganisationSettings: {
        roles: ADMIN,
        authority:
          'update_tenant_profile_controlled + merge_tenant_brand_config_controlled + merge_tenant_brand_identity_controlled; tenants',
      },
      uploadOrganisationLogoAction: {
        roles: ADMIN,
        authority:
          'uploadOrganisationLogoAction; tenant-brand storage + merge_tenant_brand_identity_controlled',
      },
      removeOrganisationLogoAction: {
        roles: ADMIN,
        authority:
          'removeOrganisationLogoAction; tenant-brand storage + merge_tenant_brand_identity_controlled',
      },
      saveAcqscRegistrationIdAction: {
        roles: ADMIN,
        authority: 'set_tenant_acqsc_registration_id_controlled; tenants',
      },
    },
  },
  '/settings/facilities': {
    classification: {},
    controls: {
      createFacility: { roles: ADMIN, authority: 'create_facility_controlled; facilities' },
      updateFacilityAction: { roles: ADMIN, authority: 'update_facility_controlled; facilities' },
      updateFacilityStatus: {
        roles: ADMIN,
        authority: 'set_facility_status_controlled; facilities',
      },
      recordFacilityRnApplicabilityAction: {
        pageIndependentCommand: true,
        roles: [...ADMIN, 'FACILITY_MANAGER'],
        authority: 'record_facility_rn_applicability_controlled; facilities',
      },
      ownLayoutPreferences: {
        roles: ADMIN,
        authority: 'layout_preferences own user row: insert/update/delete; session tenant/user RLS',
      },
    },
  },
  '/settings/notification-routing': {
    // OFF equals main (#4525): the page delegation ceiling stays main's. The N4
    // coordinator/facility-manager ceiling is static route policy that cannot
    // follow a per-tenant platform switch, so it ships only with policy-authority
    // activation. Policy controls below are inert until the editor renders.
    classification: {},
    controls: {
      ...Object.fromEntries(
        [
          'viewNotificationPolicy',
          'editNotificationPolicyDraft',
          'publishNotificationPolicy',
          'viewNotificationPolicyCoverage',
          'viewNotificationDeliveryAudit',
        ].map((id) => [
          id,
          {
            roles: [...ADMIN, 'COORDINATOR', 'FACILITY_MANAGER'],
            authority:
              'notification policy controlled RPC; canonical Settings admission plus exact current tenant/family/scope action capability, no clinical source permission',
          },
        ]),
      ),
      delegateNotificationPolicy: {
        roles: ADMIN,
        authority:
          'save_notification_policy_delegation_controlled / configure_notification_policy_review_controlled; existing tenant.configure administrator, exact scope, revision and audit guards',
      },
      previewRoutingFor: {
        pageIndependentCommand: true,
        roles: [...ADMIN, 'COORDINATOR', 'FACILITY_MANAGER'],
        authority:
          'previewRoutingFor / resolveRecipients; prepare_onboarding_nudge_event_controlled, notification_events insert/update and original recipient resolution guards; preview can record routing diagnostics, so it is a mutation under its original exact cohort',
      },
      toggleRuleActive: {
        roles: [...ADMIN, 'COORDINATOR', 'FACILITY_MANAGER'],
        pageIndependentCommand: true,
        authority:
          'toggleRuleActive; set_workflow_routing_rule_active_controlled; workflow_routing_rules original tenant/system-route/recipient guards',
      },
      manageEscalationChain: {
        roles: [...ADMIN, 'COORDINATOR', 'FACILITY_MANAGER'],
        pageIndependentCommand: true,
        authority:
          'manageEscalationChain; manage_escalation_chain_controlled; escalation_chains/steps original routing actor/facility/region/qualified-recipient guards',
      },
      configureNotificationDefaults: {
        roles: ADMIN,
        authority:
          'saveAfterHoursConfig/saveAttendanceNoShowAlertsEnabled/saveEmailComplianceRemindersEnabled/saveNotificationPosture/applyNotificationPostureToTenant/backfillTenantNotificationPreferences; upsert_tenant_config_controlled + seedNotificationPreferencesForUser; original administrator cohort, tenant configuration and session-bound privileged backfill guards',
      },
    },
  },
  '/settings/notification-routing/wizard': {
    classification: {},
    controls: {
      runSetupWizard: {
        pageIndependentCommand: true,
        roles: [...ADMIN, 'COORDINATOR', 'FACILITY_MANAGER'],
        authority:
          'runSetupWizard; replace_workflow_routing_rule_controlled, update_tenant_config_controlled / upsert_tenant_config_controlled, seed_notification_preferences_for_user_controlled; original exact wizard role/tenant and config-write cohort intersections',
      },
    },
  },
  '/settings/onboarding-packs': {
    classification: {},
    controls: {
      manageWorkerOnboardingPacks: {
        roles: ADMIN,
        authority:
          'runOnboardingPacksAction save/revise/publish/archive/item/reorder/custom-form/orientation operations; upsert/revise/publish/archive_onboarding_pack_controlled + upsert/archive_onboarding_pack_item_controlled; onboarding_packs/items and audited metadata; original tenant.configure, draft/applicability/version and role guards',
      },
    },
  },
  '/settings/onboarding/client-packs': {
    classification: { QUALITY_OFFICER: 'R', CLINICAL_LEAD: 'R' },
    controls: {
      manageClientOnboardingPacks: {
        roles: ADMIN,
        authority:
          'cloneClientPackTemplateAction/updateClientPackTemplateAction/setClientPackTemplateDefaultMappingAction/addClientPackTemplateStepAction/updateClientPackTemplateStepAction/deleteClientPackTemplateStepAction/reorderClientPackTemplateStepsAction/publishClientPackTemplateAction/archiveClientPackTemplateAction/previewClientPackTemplateAction; clone_client_onboarding_pack_template_controlled, workflow template/step controlled RPCs and workflow_template_compile_runtime_steps; workflow_templates/steps; original tenant, programme, hard-gate and published-template guards',
      },
    },
  },
  '/settings/people/assignment-requirements': {
    classification: {},
    controls: {
      publishAssignmentRequirement: {
        roles: ADMIN,
        authority:
          'previewAssignmentRequirement/confirmAssignmentRequirement; preview_worker_requirement_publication_controlled + confirm_worker_requirement_publication_controlled; original workforce.requirements.configure capability, bounded cohort, reason, actor, version/token and retained-evidence guards',
      },
    },
  },
  '/settings/document-templates': {
    classification: {},
    controls: {
      manageWorkforceDocumentTemplates: {
        roles: ADMIN,
        authority:
          'runDocumentTemplateAction workforce save/lifecycle/preview/usage; upsert_document_template_controlled / publish_document_template_controlled / archive_document_template_controlled / hr_document_template_lifecycle_controlled; original tenant.configure cohort, version/pack pinning and token validation',
      },
      saveDocumentType: {
        roles: ADMIN,
        authority:
          'manageDocumentExpiry SAVE_TYPE; save_document_type_controlled; document_types original vocabulary/privacy/tenant/audit guards',
      },
    },
  },
  '/settings/compliance': {
    classification: {},
    controls: {
      configureComplianceEscalations: {
        roles: [...ADMIN, 'QUALITY_DIRECTOR', 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
        authority:
          'runComplianceWorkAction configureComplianceEscalationAction/configureComplianceEscalationBackupAction/configureComplianceNamedRouteAction; configure_compliance_escalation_policy_controlled / configure_compliance_escalation_backup_controlled / configure_compliance_escalation_named_route_controlled; original exact escalation configuration cohort, tenant, policy version and assignee guards',
      },
      updateComplianceSetting: {
        roles: [
          ...ADMIN,
          'QUALITY_DIRECTOR',
          'QUALITY_OFFICER',
          'COMPLIANCE_OFFICER',
          'FACILITY_MANAGER',
        ],
        authority:
          'updateComplianceSetting; configure_compliance_setting_controlled; original _compliance_settings_authorise_scope facility/write and expected-version guards',
      },
      saveReviewCadences: {
        roles: [...ADMIN, 'QUALITY_DIRECTOR', 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
        authority:
          'saveComplaintCategories review-cadences; configure_compliance_settings_controlled; original reason/version/atomic batch guards',
      },
      saveIncidentSirsControls: {
        roles: [...ADMIN],
        authority:
          'saveIncidentSirsControls; configure_incident_sirs_controls_controlled; tenant-admin roles, expected version and audited tenant-wide default-off SIRS policy',
      },
      saveContractorComplianceControls: {
        roles: [...ADMIN],
        authority:
          'saveComplaintCategories contractor-compliance-controls; configure_contractor_compliance_controls_controlled; tenant-admin roles, version and audited tenant-wide policy',
      },
      saveScreeningAttestationModes: {
        roles: [...ADMIN, 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
        authority:
          'saveComplaintCategories screening-attestation-modes; configure_worker_screening_attestation_modes_controlled; action cohort intersected with screening actor cohort',
      },
      saveOutcomeLetterTemplate: {
        roles: [...ADMIN, 'QUALITY_DIRECTOR', 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
        authority:
          'saveComplaintCategories outcome-letter-template; save_complaint_outcome_letter_template_controlled; existing immutable wording and actor guards',
      },
      saveComplaintCategories: {
        roles: [...ADMIN, 'QUALITY_DIRECTOR', 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
        authority:
          'saveComplaintCategories default branch; configure_compliance_setting_controlled; complaint_categories and original scope/version guards',
      },
    },
  },
  '/settings/integrations/dhda': {
    classification: {},
    controls: {
      runDhdaFinalBetaPreview: {
        roles: B2G_VIEW_ROLES,
        authority:
          'runDhdaFinalBetaPreviewAction; canRunDhdaOperationalPreview → canViewB2gData; sealed capability preview and audited gateway execution',
      },
      stageDhdaFinalBetaReviewCandidate: {
        roles: B2G_SUBMIT_ROLES,
        authority:
          'stageDhdaFinalBetaReviewCandidateAction; canSubmitB2gData; stage_dhda_final_beta_review_candidate_controlled; AAL2 and original candidate guards',
      },
      recordDhdaFinalBetaCandidateDecision: {
        roles: B2G_SUBMIT_ROLES,
        authority:
          'recordDhdaFinalBetaCandidateDecisionAction; canSubmitB2gData; record_dhda_final_beta_candidate_decision_controlled; AAL2 and original candidate guards',
      },
      manageB2gProviderConnection: {
        roles: ADMIN,
        authority:
          'create/update B2G draft, attach/revoke credential reference, select capabilities, pause/revoke/reconnect and save provider identity profile; b2g_provider_connections/b2g_credential_references/b2g_provider_identity_profiles; controlled repository RPCs with version, scope, custody and lifecycle guards',
      },
    },
  },
  '/settings/integrations/hi': {
    classification: {},
    controls: {
      manageHiProviderConnection: {
        roles: ['OWNER', ...ADMIN],
        authority:
          'create/update HI draft, attach credential reference, select capabilities, pause/revoke/reconnect, install/check custody; hi_provider_connections/hi_credential_references and installation/checker evidence; controlled repository RPCs and secret-custody commands with existing independent checker and tenant guards',
      },
    },
  },
  '/settings/roster': {
    classification: { QUALITY_OFFICER: 'R', CLINICAL_LEAD: 'R' },
    controls: {
      manageProviderOperationalControls: {
        roles: ADMIN,
        authority:
          'previewProviderOperationalControls/saveProviderOperationalControls; preview_tenant_provider_operational_controls_controlled / patch_tenant_provider_operational_controls_controlled; existing administrator cohort, program lens, row version, impact hash, idempotency, evidence and system-admin enablement guards',
      },
      setRosterAdoptionMode: {
        roles: ADMIN,
        authority:
          'setRosterAdoptionMode; upsert_tenant_config_controlled; tenant_configs.rostering_adoption_mode and record_audit_log_controlled; original tenant.configure and expiry guards',
      },
    },
  },
  '/settings/people': {
    classification: {},
    controls: {
      configureOperationalPresentation: {
        roles: ADMIN,
        authority:
          'updateOperationalPresentation presentation branch; update_workforce_operational_presentation_controlled; original workforce.requirements.configure, module, tenant, row version and idempotency guards',
      },
      configureFirstShiftPolicy: {
        roles: ADMIN,
        authority:
          'updateOperationalPresentation POLICY_OVERRIDE_SETTINGS; policy_first_shift_requirements_controlled setter; original canConfigurePolicyOverrides and tenant/resource checks',
      },
      recordFirstShiftPolicyOverride: {
        roles: [...ADMIN, 'FACILITY_MANAGER', 'COORDINATOR'],
        authority:
          'updateOperationalPresentation POLICY_FIRST_SHIFT_OVERRIDE; record_policy_first_shift_override_controlled; original canRecordPolicyOverride, worker/self/legal-integrity restrictions',
      },
      setSmallBusinessEmployerAttestation: {
        roles: ADMIN,
        authority:
          'setCasualConversionSbeAttestation; set_casual_conversion_sbe_attestation_controlled; original SYSTEM_ADMIN/TENANT_ADMIN role cohort, tenant mismatch guard, row lock and audit evidence; main admits both roles to People settings',
      },
      manageOnboardingAdoption: {
        roles: ['TENANT_ADMIN', 'HR_DIRECTOR'],
        authority:
          'setAdverseDecisionReviewSetting onboarding_adoption; set_worker_onboarding_adoption_controlled; existing exact two-role cohort, reason and tenant guards',
      },
      manageWorkerLegalFloor: {
        roles: ['TENANT_ADMIN', 'HR_DIRECTOR'],
        authority:
          'setAdverseDecisionReviewSetting external_screening; set_worker_legal_floor_policy_controlled; existing exact two-role cohort, reason, missing-evidence mode and tenant guards',
      },
      saveOnboardingNudgeSettings: {
        roles: [...ADMIN, 'HR_DIRECTOR'],
        authority:
          'saveOnboardingNudgeSettings; save_onboarding_nudge_settings_controlled / save_learning_renewal_settings_controlled / save_people_digest_settings_controlled; revision, tenant and escalation-owner guards',
      },
    },
  },
  '/settings/modules': {
    classification: {},
    controls: {
      updateModulePreferences: {
        roles: ADMIN,
        authority:
          'updateModulePreferences/saveTenantModuleOverrides; merge_tenant_brand_config_controlled; original plan/workflow/module dependencies and tenant/platform checks; entitlement projection and audit',
      },
    },
  },
  '/settings/import': {
    classification: {},
    controls: {
      stageGovernedImportAction: {
        pageIndependentCommand: true,
        roles: [...ADMIN, 'FACILITY_MANAGER', 'COORDINATOR', 'QUALITY_OFFICER'],
        authority:
          'stage_governed_import_batch_controlled; governed_import_batches/rows; existing target, row count, validation and tenant guards',
      },
      transitionGovernedImportAction: {
        pageIndependentCommand: true,
        roles: [
          ...ADMIN,
          'OWNER',
          'FACILITY_MANAGER',
          'REGION_MANAGER',
          'COORDINATOR',
          'CASE_MANAGER',
          'QUALITY_OFFICER',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
        ],
        authority:
          'transition_governed_import_batch_controlled; governed_import_batches/rows; original per-status reviewer and tenant guards',
      },
      applyGovernedImportAction: {
        pageIndependentCommand: true,
        roles: [
          ...ADMIN,
          'OWNER',
          'FACILITY_MANAGER',
          'REGION_MANAGER',
          'COORDINATOR',
          'CASE_MANAGER',
          'QUALITY_OFFICER',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'ENROLLED_NURSE',
        ],
        authority:
          'apply_governed_import_batch_controlled; governed_import_batches/rows and domain-controlled import processors; approved/zero-error/target-role checks',
      },
    },
  },
  '/settings/feature-flags': {
    classification: {},
    controls: {
      setFeatureFlag: {
        roles: ADMIN,
        authority:
          'set_feature_flag_controlled; feature_flags; existing SYSTEM_ADMIN Settings destination and tenant/global/containment constraints',
      },
      saveTenantRolloutState: {
        pageIndependentCommand: true,
        roles: ADMIN,
        authority:
          'patch_tenant_rollout_state_controlled; tenant_rollout_states; existing platform cross-tenant authority, expected version and evidence',
      },
    },
  },
  '/settings/holidays': {
    classification: {},
    controls: {
      savePublicHolidayAction: {
        roles: ADMIN,
        authority: 'upsert_public_holiday_controlled; public_holidays; tenant-local facility scope',
      },
      deletePublicHolidayAction: {
        roles: ADMIN,
        authority:
          'delete_public_holiday_controlled; public_holidays; tenant-local row and confirmation',
      },
    },
  },
  '/settings/cm-task-types': {
    classification: {},
    controls: {
      saveCmTaskTypeAction: {
        roles: ADMIN,
        authority: 'upsert_cm_task_type_controlled; cm_task_types',
      },
      archiveCmTaskTypeAction: {
        roles: ADMIN,
        authority: 'archive_cm_task_type_controlled; cm_task_types',
      },
    },
  },
  '/settings/service-execution': {
    classification: {},
    controls: {
      setServiceExecutionPackState: {
        roles: ADMIN,
        authority:
          'set_service_execution_v2_pack_state_controlled; service_execution_v2_pack_flags; shadow-before-enforce and stated reason',
      },
    },
  },
  '/settings/portal': {
    classification: {},
    controls: {
      saveTenantPortalConfiguration: {
        roles: ADMIN,
        authority:
          'update_tenant_config_controlled portal_config; tenant_configs; mandatory section normalization',
      },
    },
  },
  '/settings/assessments': {
    classification: {},
    controls: {
      setAssessmentSafetyHandoffPolicy: {
        roles: ADMIN,
        authority:
          'set_assessment_safety_policy_controlled; configure_provider_owner_now_controlled; configure_provider_owner_controlled; audited default-off handoff and shared accountable-owner/delegation configuration',
      },
      setAssessmentInstrumentEnabled: {
        roles: ADMIN,
        authority:
          'set_tenant_assessment_instrument_enabled_controlled; tenant_assessment_instrument_settings; existing action administrator check',
      },
      approvePlatformAssessmentInstrument: {
        roles: ['SYSTEM_ADMIN'],
        authority:
          'approve_platform_assessment_instrument_controlled; platform template and RN sign-off',
      },
    },
  },
  '/settings/funding-authorisations': {
    classification: {},
    controls: {
      manageFundingAuthorisations: {
        roles: [...ADMIN, 'FINANCE_OFFICER'],
        authority:
          'createFundingAuthorisation/create_funding_authorisation_controlled; importFundingAuthorisationsCsv/import_funding_authorisations_controlled; updateFundingAuthorisation/update_funding_authorisation_controlled; supersedeFundingAuthorisation/supersede_funding_authorisation_controlled; existing finance actor and SQL region scope',
      },
    },
  },
  '/settings/data-management': {
    classification: {},
    controls: {
      createDataDeletionRequestAction: {
        roles: ADMIN,
        authority:
          'create_data_deletion_request_controlled; data_deletion_requests; existing tenant.configure actor and tenant client check',
      },
      processDataDeletionRequestAction: {
        roles: ADMIN,
        authority:
          'process_client_privacy_request_controlled atomic hold checks, redaction and completion + private export artifact; existing tenant.configure actor and pending request check',
      },
    },
  },
  '/settings/webhooks': {
    classification: {},
    controls: {
      createWebhookApiKeyAction: {
        roles: ['SYSTEM_ADMIN'],
        authority:
          'webhook_api_keys insert; tenant_admin_manage_webhook_keys RLS; existing SYSTEM_ADMIN action check',
      },
      revokeWebhookApiKeyAction: {
        roles: ['SYSTEM_ADMIN'],
        authority:
          'webhook_api_keys update is_active; tenant_admin_manage_webhook_keys RLS; existing SYSTEM_ADMIN action check',
      },
      retryWebhookDeliveryAction: {
        roles: ['SYSTEM_ADMIN'],
        authority:
          'webhook_delivery_logs update; tenant_isolation_webhook_delivery_logs RLS; existing SYSTEM_ADMIN action check',
      },
    },
  },
  '/settings/webhooks/[id]/mapping': {
    classification: {},
    controls: {
      saveWebhookFieldMappingAction: {
        roles: ['SYSTEM_ADMIN'],
        authority:
          'webhook_api_keys metadata update; tenant_admin_manage_webhook_keys RLS; existing SYSTEM_ADMIN action check',
      },
    },
  },
  '/settings/workflows': {
    classification: {},
    controls: {
      createWorkflowTemplate: {
        roles: [...ADMIN, 'FACILITY_MANAGER'],
        authority: 'create_workflow_template_controlled; workflow_templates',
      },
      updateWorkflowTemplate: {
        roles: ADMIN,
        authority: 'legacy_update_workflow_template_controlled; workflow_templates',
      },
      deleteWorkflowTemplate: {
        roles: ADMIN,
        authority: 'delete_workflow_draft_template_controlled; workflow_templates',
      },
      saveTenantWorkflowConfiguration: {
        roles: ADMIN,
        authority:
          'saveTenantWorkflowConfiguration; merge_tenant_brand_config_controlled + record_audit_log_controlled',
      },
    },
  },
  '/settings/workflows/[id]': {
    classification: {},
    controls: {
      updateWorkflowTemplate: {
        roles: ADMIN,
        authority: 'legacy_update_workflow_template_controlled; workflow_templates',
      },
    },
  },
  '/settings/registration-providers': {
    classification: {},
    controls: {
      manageRegistrationProviders: {
        roles: ADMIN,
        authority:
          'runRegistrationProvidersAction typed commands; registration configuration, provider profile/associations, event grant/read-audit/revocation, schedules, external contracts/registration/visibility facts, programme drift review controlled RPCs; fixed ADMIN Settings cohort and all original ownership/version/evidence/tenant guards. Assigned programme task reviews retain independent task actor/RPC scope.',
      },
    },
  },
  '/settings/integrations/xero': {
    classification: {},
    controls: {
      connectXero: {
        roles: ['OWNER', 'TENANT_ADMIN', 'FINANCE_OFFICER', 'SYSTEM_ADMIN'],
        authority:
          'GET /api/xero/authorize; ALLOWED_ROLES; record_xero_connection_initiated OAuth nonce and audit; original actor tenant and credential guards',
      },
      saveSahInvoiceSplitMode: {
        roles: ADMIN,
        authority:
          'saveSahInvoiceSplitModeAction; upsert_tenant_config_controlled sah_invoice_split_mode; existing tenant-configuration cohort and validation',
      },
    },
  },
  '/settings/integrations/xero/wizard/[[...step]]': {
    classification: {},
    controls: {
      confirmXeroAccountMapping: {
        roles: ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'],
        authority:
          'runXeroArAction confirmAccountCodeMapping; record_account_code_mapping_confirmed; finance cohort and connection/program scope',
      },
      confirmXeroTaxMapping: {
        roles: ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'],
        authority:
          'runXeroArAction confirmTaxRateMapping; record_tax_rate_mapping_confirmed; finance cohort and connection/program scope',
      },
      confirmXeroContactMapping: {
        roles: ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'],
        authority:
          'runXeroArAction confirmContactMapping; record_contact_mapping_confirmed; finance cohort and connection/client scope',
      },
      createXeroContact: {
        roles: ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'],
        authority:
          'runXeroArAction createContactInXero; Xero accounting Contacts POST, token/call accounting and record_contact_created_in_xero; finance, tenant, client and connection guards',
      },
    },
  },
  '/settings/integrations/xero/backfill': {
    classification: {},
    controls: {
      backfillXeroInvoices: {
        roles: ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'],
        authority:
          'runXeroArAction bulkBackfillInvoicePushes; record_invoices_backfill_push_controlled; finance cohort, bounded dates/program/eligible invoice and queue guards',
      },
    },
  },
  '/settings/billing': {
    classification: { FINANCE_OFFICER: 'R' },
    controls: {
      manageBillingSubscription: {
        roles: [...ADMIN, 'FINANCE_OFFICER'],
        authority:
          'createCheckoutSession / createPortalSession / updateSubscriptionQuantity; Stripe customer/checkout/portal/subscription operations and billing self-service/attestation controlled RPCs; existing actor tenant, legal acceptance, plan/quantity guards',
      },
      manageChargeCatalogue: {
        pageIndependentCommand: true,
        roles: [...ADMIN, 'OWNER', 'FINANCE_OFFICER', 'REGION_FINANCE_OFFICER'],
        authority:
          'upsertChargeCatalogueEntry; upsert_billing_charge_catalogue_entry_controlled; existing finance module/cohort and charge validation',
      },
      managePlatformPricing: {
        roles: ['SYSTEM_ADMIN'],
        authority:
          'saveEssentialsListPrice / saveEssentialsTenantOverride; set_essentials_list_price_controlled / set_essentials_tenant_price_override_controlled; independent platform allowlist and SUPERADMIN capability',
      },
    },
  },
  '/settings/onboarding/readiness': {
    classification: {},
    controls: {
      setClientReadinessPolicy: {
        roles: ADMIN,
        authority:
          'setClientOnboardingReadinessPolicyAction; update_client_onboarding_provider_settings_controlled; original tenant.configure cohort and ready-evidence validation',
      },
    },
  },
  '/settings/onboarding/worker/base': {
    classification: {},
    controls: {
      manageWorkerBaseJourney: {
        roles: ADMIN,
        authority:
          'setOnboardingStepConfig / setVaccinationPolicyConfig; set_tenant_onboarding_step_config_controlled / set_tenant_onboarding_step_metadata_controlled; original administrator cohort and locked/role-locked/blocking floors',
      },
    },
  },
  '/settings/onboarding/screening': {
    classification: {},
    controls: {
      manageScreeningMatrix: {
        pageIndependentCommand: true,
        roles: [
          ...ADMIN,
          'REGION_MANAGER',
          'REGION_COMPLIANCE_OFFICER',
          'FACILITY_MANAGER',
          'QUALITY_OFFICER',
          'COMPLIANCE_OFFICER',
          'HR_DIRECTOR',
        ],
        authority:
          'saveScreeningMatrixRuleAction; set_worker_screening_matrix_rule_controlled; original ROLES, tenant, reason and legal floors',
      },
      manageAdverseScreeningReview: {
        roles: ADMIN,
        authority:
          'setAdverseDecisionReviewSetting default; set_worker_screening_adverse_second_person_controlled; original administrator cohort and reason validation',
      },
    },
  },
  '/settings/onboarding/dms-cutover': {
    classification: {},
    controls: {
      manageOnboardingDmsCutover: {
        roles: ADMIN,
        authority:
          'runOnboardingDmsDryRun / runOnboardingDmsJourneyOperation / setOnboardingDmsProjectionFlag; compile_worker_onboarding_journey_requirements_controlled / reconcile_worker_onboarding_journey_requirements_controlled / reconcile_onboarding_requirement_document_mappings_controlled / set_feature_flag_controlled; original administrator cohort, Phase 0b blockers and rollback policy',
      },
    },
  },
  '/settings/onboarding/induction-policies': {
    classification: {},
    controls: {
      manageInductionPolicy: {
        roles: [
          ...ADMIN,
          'FACILITY_MANAGER',
          'HR_DIRECTOR',
          'HR_OFFICER',
          'COORDINATOR',
          'QUALITY_OFFICER',
        ],
        authority:
          'setPolicyEnforcementLevel / setPolicyInductionFlag Settings invocation; set_policy_enforcement_level_controlled / set_policy_induction_flag_controlled; existing POLICY_CONFIGURATION_ROLES and published policy/tenant guards; operational policy invocations retain SQL scope',
      },
    },
  },
  '/settings/system': {
    classification: {},
    controls: {
      manageSettingsVisibility: {
        roles: ADMIN,
        authority:
          'saveSettingsVisibilityAction; layout_preferences update/insert with existing tenant/user/pref-key scope and locked leaves',
      },
      manageSettingsDelegation: {
        roles: ADMIN,
        authority:
          'saveSettingsDelegationAction; layout_preferences update/insert with existing tenant/user/pref-key scope and permitted role/cluster normalisation',
      },
      updateOrganisationTimezone: {
        roles: ADMIN,
        authority:
          'updateOrganisationTimezoneAction; upsert_tenant_config_controlled; original administrator cohort and timezone validation',
      },
    },
  },
  '/settings/integrations/services-australia': {
    classification: {},
    controls: {
      manageServicesAustraliaConfiguration: {
        roles: [...ADMIN, 'OWNER'],
        authority:
          'Services Australia Settings typed provider, REST, PRODA lifecycle/NOI receipt and reviewed NOI commands; original SETTINGS_SERVICES_AUSTRALIA_ROLES, permit/attestation, programme, maker/checker, engineering freeze, agency custody and protected RPC/transport contracts',
      },
      approveServicesAustraliaProductionCutover: {
        roles: ['SYSTEM_ADMIN'],
        authority:
          'recordServicesAustraliaProductionCutoverApprovalAction; existing exact SETTINGS_SYSTEM_OPERATOR_ROLES, controlled cutover receipt/verification RPCs and independent evidence custody',
      },
    },
  },
  '/settings/integrations/services-australia/mapping': {
    classification: {},
    controls: {
      manageServicesAustraliaMappings: {
        roles: [...ADMIN, 'OWNER'],
        authority:
          'Services Australia provider-service search/inspect/reference refresh, propose/verify/approve/activate mapping commands; original SETTINGS_SERVICES_AUSTRALIA_ROLES and provider scope, agency read/receipt/projection and controlled mapping/capability RPCs',
      },
    },
  },
  '/settings/integrations': {
    classification: {},
    controls: {
      monitorGovernmentConnections: {
        roles: ['OWNER', 'TENANT_ADMIN'],
        authority:
          'governmentMonitoringAction; get_government_integration_monitoring_controlled; audited export writes government integration audit records, with original exact OWNER/TENANT_ADMIN, enabled integration, bounded scope and recovery observation guards',
      },
      setGovernmentIntegration: {
        roles: ['OWNER', 'TENANT_ADMIN'],
        authority:
          'setGovernmentIntegrationAction switch; set_tenant_government_integration_controlled; exact existing owner/tenant-admin cohort, plan entitlements and MFA prerequisites',
      },
      manageGovernmentPermit: {
        pageIndependentCommand: true,
        roles: [...ADMIN, 'OWNER'],
        authority:
          'saveGovernmentIntegrationPermitAction; update_tenant_government_integration_permit_draft_controlled; existing canConfigureGovernmentProviderSetup and enabled/capability scope',
      },
    },
  },
  '/settings/integrations/services-australia/conformance': {
    classification: {},
    controls: {
      runServicesAustraliaConformance: {
        roles: [
          'OWNER',
          ...ADMIN,
          'COORDINATOR',
          'QUALITY_OFFICER',
          'COMPLIANCE_OFFICER',
          'FINANCE_OFFICER',
        ],
        authority:
          'Conformance Settings calls to runServicesAustraliaClientGovernmentRecordAction / runServicesAustraliaClientGovernmentExpectedErrorAction / loadServicesAustraliaClientIdentifierByKeyAction; existing SERVICES_AUSTRALIA_INTEGRATION_ROLES, client/tenant, supported test row, Vendor/iTEST, confirmation, permit/custody and engineering freeze; controlled operation/projection/ledger RPCs and separately governed agency transport. Operational client-record calls retain independent scope. Release 1 DEFERRED_OWNER: NOI source frozen until DTSS closes 12 Oct 2026; main command authority retained; Settings narrowing not applied.',
      },
    },
  },
  '/settings/users': {
    classification: {},
    controls: {
      manageUserAccounts: {
        roles: ADMIN,
        authority:
          'createUserWithAuth / updateUserAction / deactivateUser / adminResetPassword / clearEmailInvalidFlagAction / resetUserMfa / setTenantUserMfaProfileStatus; users/staff/organisations tenant rows, controlled identity/session/audit RPCs, Auth admin and account-setup email/suppression effects; original role, target-tenant, escalation and verification checks',
      },
      ownMfa: {
        roles: ADMIN,
        authority:
          'own Auth MFA factor enrol/challenge/verify/unenrol; setOwnMfaProfileStatus own-user verified-factor and tenant guards; personal auth lane remains independently available',
      },
      ownLayoutPreferences: {
        roles: ADMIN,
        authority: 'layout_preferences own user row: insert/update/delete; session tenant/user RLS',
      },
    },
  },
  '/settings/signature-templates': {
    classification: { QUALITY_OFFICER: 'R', CLINICAL_LEAD: 'R' },
    controls: {
      readSignatureTemplates: {
        roles: [
          ...ADMIN,
          'FACILITY_MANAGER',
          'FINANCE_OFFICER',
          'COORDINATOR',
          'CASE_MANAGER',
          'QUALITY_OFFICER',
          'CLINICAL_LEAD',
        ],
        authority:
          'settingsListSignatureDocumentTemplates; list_signature_document_templates_controlled original reader cohort and resource scope',
      },
      previewSignatureTemplateDraft: {
        roles: USER_ROLE_VALUES,
        authority:
          'settingsPreviewSignatureDocumentTemplateDraft → previewSignatureDocumentTemplateDraft; authenticated signing context, rate limiter and local contract validation; main has no role guard',
      },
      createSignatureDocumentTemplateDraft: {
        roles: [...ADMIN, 'QUALITY_OFFICER', 'CLINICAL_LEAD'],
        authority:
          'createSignatureDocumentTemplateDraft; original controlled lifecycle RPC actor cohort, resource-type manager, tenant and content guards retained',
      },
      versionSignatureDocumentTemplate: {
        roles: [...ADMIN, 'QUALITY_OFFICER', 'CLINICAL_LEAD'],
        authority:
          'versionSignatureDocumentTemplate; original controlled lifecycle RPC actor cohort, resource-type manager, tenant and content guards retained',
      },
      saveSignatureTemplateDraft: {
        roles: [...ADMIN, 'QUALITY_OFFICER', 'CLINICAL_LEAD'],
        authority:
          'saveSignatureTemplateDraft; original controlled lifecycle RPC actor cohort, resource-type manager, tenant and content guards retained',
      },
      submitSignatureDocumentTemplateVersion: {
        roles: [...ADMIN, 'QUALITY_OFFICER', 'CLINICAL_LEAD'],
        authority:
          'submitSignatureDocumentTemplateVersion; original controlled lifecycle RPC actor cohort, resource-type manager, tenant and content guards retained',
      },
      readSignatureTemplateVersion: {
        roles: [...ADMIN, 'QUALITY_OFFICER', 'CLINICAL_LEAD'],
        authority:
          'settingsLoadSignatureDocumentTemplateVersion → loadSignatureDocumentTemplateVersion; get_signature_document_template_version_controlled original reader cohort; support and tenant scope retained',
      },
      decideSignatureDocumentTemplateVersion: {
        roles: ADMIN,
        authority: 'decide_signature_document_template_version_controlled; independent checker',
      },
      signatureTemplateSettings: {
        roles: ADMIN,
        authority:
          'signature_template_settings_controlled; original administrator-only template settings cohort, tenant scope and settings payload guards',
      },
      tenantSigningSettings: {
        roles: ADMIN,
        authority: 'runDocumentTemplateAction tenantSigningSettings / saveTenantSigningSettings',
      },
      signatureOtpSettings: {
        roles: ADMIN,
        authority: 'runDocumentTemplateAction signatureOtpSettings / saveSignatureOtpSettings',
      },
    },
  },
  '/settings/security': {
    classification: { COMPLIANCE_OFFICER: 'R' },
    controls: {
      ownMfa: { roles: ADMIN, authority: 'auth.mfa personal session; enrol / verify / unenrol' },
      adminMfa: { roles: ['TENANT_ADMIN'], authority: 'setTenantAdminMfaRequired' },
      idleTimeout: { roles: ['TENANT_ADMIN'], authority: 'saveSessionIdleTimeoutMinutes' },
      b2gFrequency: { roles: ['TENANT_ADMIN'], authority: 'B2gMfaFrequency existing action' },
    },
  },
  '/settings/retention': {
    classification: { COMPLIANCE_OFFICER: 'R' },
    controls: {
      ownLayoutPreferences: {
        roles: [...ADMIN, 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
        authority:
          'layout_preferences own user row: insert/update/delete; session tenant/user RLS and existing retention destination cohort',
      },
      saveDocumentReminders: {
        pageIndependentCommand: true,
        roles: [...ADMIN, 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
        authority:
          'manageDocumentExpiry SAVE_REMINDERS; save_document_reminder_settings_controlled; document_reminder_settings tenant and audit guards',
      },
      saveRetentionExtension: {
        pageIndependentCommand: true,
        roles: [...ADMIN, 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
        authority:
          'manageDocumentExpiry SAVE_RETENTION; save_retention_extension_controlled; retention_policies/actions original minimum/source/accountable activation and audit guards',
      },
      runRetentionArchives: {
        roles: ADMIN,
        authority:
          'archive-runner-internal actor check: SYSTEM_ADMIN/TENANT_ADMIN only; screen action permanently rehearses',
      },
    },
  },
};

/** Design R classifications describe handoffs; they never override read or write guards. */
const READ_HANDOFF_CLASSIFICATIONS: Readonly<
  Record<string, Readonly<Record<string, Classification>>>
> = {
  '/settings/organisation': {
    FACILITY_MANAGER: 'R',
    REGION_MANAGER: 'R',
  },
  '/settings/facilities': {
    FACILITY_MANAGER: 'R',
    REGION_MANAGER: 'R',
  },
  '/settings/portal': {
    FACILITY_MANAGER: 'R',
    REGION_MANAGER: 'R',
  },
  '/settings/holidays': {
    FACILITY_MANAGER: 'R',
    REGION_MANAGER: 'R',
  },
  '/settings/funding-authorisations': {
    FACILITY_MANAGER: 'R',
    REGION_MANAGER: 'R',
  },
  '/settings/facility-config': {
    REGION_MANAGER: 'R',
  },
  '/settings/workflows': {
    QUALITY_OFFICER: 'R',
    CLINICAL_LEAD: 'R',
  },
  '/settings/assessments': {
    QUALITY_OFFICER: 'R',
    CLINICAL_LEAD: 'R',
  },
  '/settings/roster': {
    QUALITY_OFFICER: 'R',
    CLINICAL_LEAD: 'R',
  },
  '/settings/service-execution': {
    QUALITY_OFFICER: 'R',
    CLINICAL_LEAD: 'R',
  },
  '/settings/signature-templates': {
    QUALITY_OFFICER: 'R',
    CLINICAL_LEAD: 'R',
  },
  '/settings/onboarding/client-packs': {
    QUALITY_OFFICER: 'R',
    CLINICAL_LEAD: 'R',
  },
  '/settings/retention': {
    COMPLIANCE_OFFICER: 'R',
  },
  '/settings/data-management': {
    COMPLIANCE_OFFICER: 'R',
  },
  '/settings/security': {
    COMPLIANCE_OFFICER: 'R',
  },
  '/settings/notification-routing': {
    COORDINATOR: 'R',
    FACILITY_MANAGER: 'R',
  },
  '/settings/notification-routing/wizard': {
    COORDINATOR: 'R',
    FACILITY_MANAGER: 'R',
  },
  '/settings/notification-audit': {
    COORDINATOR: 'R',
    FACILITY_MANAGER: 'R',
  },
  '/settings/notifications/log': {
    COORDINATOR: 'R',
    FACILITY_MANAGER: 'R',
  },
  '/settings/integrations/xero': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/integrations/xero/wizard': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/integrations/xero/backfill': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/payroll': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/payroll/profile': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/payroll/provider-responsibilities': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/payroll/pay-roles': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/payroll/eba': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/import': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/billing': {
    FINANCE_OFFICER: 'R',
  },
  '/settings/modules': {
    FINANCE_OFFICER: 'R',
  },
};

export function settingsDelegationClassification(leafId: string, role: string): Classification {
  if (isSettingsDeferredOwnerPath(leafId)) return 'N';
  return (
    SETTINGS_DELEGATED_AUTHORITY[leafId]?.classification[role] ??
    READ_HANDOFF_CLASSIFICATIONS[leafId]?.[role] ??
    'N'
  );
}

/** Explicit E grant is part of the Settings route policy, never a generic role alias. */
export function isGrantedEndToEndSettingsRole(
  pathname: string,
  role: string,
  delegation?: SettingsDelegationPreference | null,
) {
  if (settingsDelegationClassification(pathname, role) !== 'E') return false;
  const match = findClusterForPath(pathname);
  const cluster = match?.item.delegationClusterId ?? match?.cluster.id;
  return Boolean(
    cluster &&
    delegation?.grantedRolesByCluster[cluster]?.includes(role) &&
    !delegation.deniedRolesByCluster[cluster]?.includes(role),
  );
}
